// Per-locale name editor (i18n S4.3).
//
// Given an entity's translation key and its current per-locale name map
// (both come from the cycle graph — GET /admin/cycle returns name_key +
// names), this renders one input per supported content locale and saves
// edits straight into the translations catalogue via
// POST /admin/cycle/translations.
//
// Locales offered = backend's supported set (/i18n/locales) ∪ whatever the
// entity already has a translation for.  Adding a brand-new locale is as
// simple as typing its tag — the catalogue is open (zh, ur, …).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorBanner } from "@/components/ErrorBanner";
import { adminCycleApi, i18nApi } from "@/lib/api";
import type { LocaleNames } from "@/lib/types";

export function NameTranslationsEditor({
  nameKey,
  names,
}: {
  /** translations key, e.g. "stage.STAGE_3.name". Null → entity predates
   *  key-ification; nothing to edit until it's re-imported with a key. */
  nameKey: string | null | undefined;
  /** current per-locale texts from the cycle graph */
  names: LocaleNames | undefined;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const localesQ = useQuery({
    queryKey: ["i18n", "locales"],
    queryFn: () => i18nApi.locales(),
    staleTime: 60_000,
  });

  // Union of supported locales and the ones this entity already has.
  const locales = useMemo(() => {
    const set = new Set<string>(localesQ.data?.locales ?? []);
    for (const l of Object.keys(names ?? {})) set.add(l);
    return [...set].sort();
  }, [localesQ.data, names]);

  // Local draft of the texts; seeded from `names`, re-seeded when the
  // entity (key) or its catalogue values change.
  const [draft, setDraft] = useState<Record<string, string>>({});
  useEffect(() => {
    setDraft({ ...(names ?? {}) });
  }, [nameKey, names]);

  const [newLocale, setNewLocale] = useState("");

  const save = useMutation({
    mutationFn: (entries: Array<{ key: string; locale: string; text: string }>) =>
      adminCycleApi.upsertTranslations(entries),
    onSuccess: () => {
      // Graph carries name/names; stage detail carries resolved name.
      qc.invalidateQueries({ queryKey: ["cycle", "graph"] });
      qc.invalidateQueries({ queryKey: ["stage"] });
      qc.invalidateQueries({ queryKey: ["stages"] });
    },
  });

  if (!nameKey) {
    return (
      <p className="text-xs text-text-muted italic">{t("nameI18n.noKey")}</p>
    );
  }

  // Only send locales whose text changed and is non-empty (the upsert
  // endpoint rejects empty strings).
  const changed = locales
    .filter((l) => (draft[l] ?? "").trim() && (draft[l] ?? "").trim() !== (names?.[l] ?? ""))
    .map((l) => ({ key: nameKey, locale: l, text: draft[l]!.trim() }));

  function addLocale() {
    const l = newLocale.trim().toLowerCase();
    if (!l) return;
    setDraft((d) => ({ ...d, [l]: d[l] ?? "" }));
    setNewLocale("");
  }

  const rows = [...new Set([...locales, ...Object.keys(draft)])].sort();

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-mono uppercase tracking-wider text-text-muted">
          {t("nameI18n.title")}
        </h4>
        <code className="text-[10px] text-text-muted">{nameKey}</code>
      </div>

      {save.isError && (
        <ErrorBanner onDismiss={() => save.reset()}>
          {save.error instanceof Error ? save.error.message : t("common.error")}
        </ErrorBanner>
      )}

      <div className="space-y-1.5">
        {rows.map((l) => (
          <label key={l} className="flex items-center gap-2">
            <span className="w-10 font-mono text-xs uppercase text-text-muted">{l}</span>
            <input
              className="flex-1 px-3 py-1.5 rounded-md bg-surface border border-border text-sm"
              value={draft[l] ?? ""}
              onChange={(e) => setDraft((d) => ({ ...d, [l]: e.target.value }))}
              placeholder={names?.[l] ?? ""}
            />
          </label>
        ))}
      </div>

      <div className="flex items-center gap-2 pt-1">
        <input
          className="w-28 px-3 py-1.5 rounded-md bg-surface border border-border font-mono text-xs"
          value={newLocale}
          onChange={(e) => setNewLocale(e.target.value)}
          placeholder={t("nameI18n.addLocale")}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addLocale();
            }
          }}
        />
        <button type="button" className="btn text-xs" onClick={addLocale}>
          {t("nameI18n.add")}
        </button>
        <button
          type="button"
          className="btn btn-primary text-xs ml-auto"
          disabled={changed.length === 0 || save.isPending}
          onClick={() => save.mutate(changed)}
        >
          {save.isPending ? t("nameI18n.saving") : t("nameI18n.save")}
        </button>
      </div>
    </div>
  );
}
